import { useCallback, useMemo, useState } from 'react'

import { useCartInfoStore, useCartStore } from '@entities/Cart'

export interface UseBonusManagerReturn {
  // Состояние бонусов
  availableBonuses: number
  appliedBonuses: number
  maxBonusesToUse: number
  hasAppliedBonuses: boolean
  canApplyBonuses: boolean
  
  // Действия
  applyBonuses: (amount: number) => Promise<void>
  clearBonuses: () => Promise<void>
  
  // Состояние загрузки
  isBusy: boolean
  isLoading: boolean
}

export function useBonusManager (): UseBonusManagerReturn {
  const { setAppliedBonuses, clearAppliedBonuses: clearCartBonuses } = useCartStore()
  const cartInfo = useCartInfoStore((state) => state.cartInfo)
  const cartInfoLoading = useCartInfoStore((state) => state.cartInfoLoading)
  const refetchCartInfo = useCartInfoStore((state) => state.refetchCartInfo)
  
  const [ isBusy, setIsBusy ] = useState(false)
  
  // Получаем bonus offer из saleOffers (как в мобильном приложении)
  const bonusOffer = useMemo(
    () => cartInfo?.saleOffers?.find((offer) => offer.type === 'bonus'),
    [ cartInfo?.saleOffers ]
  )
  
  // Получаем bonusCurrencyId для применения бонусов (как в customer-app)
  const bonusCurrencyId = useMemo(() => {
    const bonusSale = cartInfo?.sales?.find((sale) => sale.type === 'bonus' && sale.bonusCurrencyId)
    if (bonusSale?.bonusCurrencyId) {
      return bonusSale.bonusCurrencyId
    }

    return bonusOffer?.discountCurrencyId ?? null
  }, [ cartInfo?.sales, bonusOffer ])
  
  // Получаем примененные бонусы из sales
  const appliedBonusesTotal = useMemo(() => {
    const bonusSales = cartInfo?.sales?.filter((sale) => sale.type === 'bonus') || []
    return bonusSales.reduce((acc, sale) => acc + (sale.amount || 0), 0)
  }, [ cartInfo?.sales ])
  
  // Максимум бонусов доступных для списания
  const maxSpendableBonuses = Math.max(bonusOffer?.maxDiscount ?? 0, appliedBonusesTotal)
  const availableBonuses = Math.max(0, Math.floor(maxSpendableBonuses))
  const appliedBonuses = Math.max(0, Math.round(appliedBonusesTotal))
  
  // Проверяем, применены ли бонусы
  const hasAppliedBonuses = appliedBonuses > 0
  const canApplyBonuses = availableBonuses > 0
  
  const applyBonuses = useCallback(async (amount: number) => {
    const normalizedAmount = Math.min(
      Math.max(Math.round(amount), 0),
      availableBonuses
    )
    
    if (normalizedAmount <= 0) {
      throw new Error('Сумма должна быть больше нуля')
    }
    
    setIsBusy(true)
    
    try {
      // Применяем бонусы как в мобильном приложении
      setAppliedBonuses([ {
        bonusCurrencyId: String(bonusCurrencyId ?? '0'),
        amount: normalizedAmount
      } ])
      
      if (refetchCartInfo) {
        await refetchCartInfo()
      }
      
    }
    catch (error) {
      throw new Error('Ошибка при применении бонусов')
    }
    finally {
      setIsBusy(false)
    }
  }, [ availableBonuses, bonusCurrencyId, setAppliedBonuses, refetchCartInfo ])
  
  const clearBonuses = useCallback(async () => {
    if (!hasAppliedBonuses && appliedBonusesTotal <= 0) {
      return
    }
    
    setIsBusy(true)
    
    try {
      // Очищаем бонусы как в мобильном приложении
      clearCartBonuses()
      
      if (refetchCartInfo) {
        await refetchCartInfo()
      }
      
    }
    catch (error) {
      throw new Error('Ошибка при отмене бонусов')
    }
    finally {
      setIsBusy(false)
    }
  }, [ hasAppliedBonuses, appliedBonusesTotal, clearCartBonuses, refetchCartInfo ])
  
  return {
    availableBonuses,
    appliedBonuses,
    maxBonusesToUse: availableBonuses,
    hasAppliedBonuses,
    canApplyBonuses,
    applyBonuses,
    clearBonuses,
    isBusy,
    isLoading: cartInfoLoading
  }
}